import moment from 'moment-timezone';
import {PantryGroupDto, BalanceTypes} from 'src/types/serverTypes/Account';
import TransactionStore from 'src/stores/TransactionStore';
import {PickUpTimeType} from 'src/types/Menu';

export const DAYS = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
} as const;

export type DayOfWeek = (typeof DAYS)[keyof typeof DAYS];

const DAY_NAMES: {[key: string]: DayOfWeek} = {
  Mon: DAYS.MONDAY,
  Tue: DAYS.TUESDAY,
  Wed: DAYS.WEDNESDAY,
  Thu: DAYS.THURSDAY,
  Fri: DAYS.FRIDAY,
  Sat: DAYS.SATURDAY,
  Sun: DAYS.SUNDAY,
};

export interface DayTimeRestriction {
  dayOfWeek: DayOfWeek;
  timeRanges: Array<{start: string; end: string}>; // HH:mm format
}

const restrictionsCache = new Map<string, DayTimeRestriction[]>();

export const parseDayTimeRestrictions = (
  dayTimeRestrictions?: string | null,
): DayTimeRestriction[] => {
  if (!dayTimeRestrictions?.trim()) {
    return [];
  }

  const trimmed = dayTimeRestrictions.trim();

  if (restrictionsCache.has(trimmed)) {
    return restrictionsCache.get(trimmed)!;
  }

  const restrictions: DayTimeRestriction[] = [];

  const DAY_PATTERN = /(\w{3}):\s*"?([^;]*?)"?(?:;|$)/g;
  const TIME_PATTERN = /(\d{1,2}:\d{2})-(\d{1,2}:\d{2})/g;

  let match: RegExpExecArray | null;

  while ((match = DAY_PATTERN.exec(trimmed)) !== null) {
    const [, dayName, timeString] = match;
    const dayOfWeek = DAY_NAMES[dayName];

    if (dayOfWeek === undefined) {
      continue;
    }

    const timeRanges: Array<{start: string; end: string}> = [];
    const cleanedTimeString = timeString.trim().replace(/"/g, '');

    if (cleanedTimeString && cleanedTimeString !== '""') {
      let timeMatch: RegExpExecArray | null;
      const timePattern = new RegExp(TIME_PATTERN);

      while ((timeMatch = timePattern.exec(cleanedTimeString)) !== null) {
        timeRanges.push({
          start: timeMatch[1],
          end: timeMatch[2],
        });
      }
    }

    restrictions.push({dayOfWeek, timeRanges});
  }

  restrictionsCache.set(trimmed, restrictions);
  return restrictions;
};

const isFullDayRange = (
  timeRanges: Array<{start: string; end: string}>,
): boolean => {
  return timeRanges.some(
    (range) => range.start === '00:00' && range.end === '23:59',
  );
};

const isTimeInRanges = (
  time: string,
  timeRanges: Array<{start: string; end: string}>,
): boolean => {
  if (timeRanges.length === 0) {
    return false;
  }

  const [hours, minutes] = time.split(':').map(Number);
  const timeMinutes = hours * 60 + minutes;

  return timeRanges.some((range) => {
    const [startH, startM] = range.start.split(':').map(Number);
    const [endH, endM] = range.end.split(':').map(Number);

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (startMinutes <= endMinutes) {
      return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
    }

    return timeMinutes >= startMinutes || timeMinutes <= endMinutes;
  });
};

const getPickupTimeInLocationTimezone = (
  pickupTime: {
    date?: string;
    time?: string;
    timeString?: string;
    dayOfWeek?: number;
  } | null,
  locationTimezone: string,
): moment.Moment | null => {
  if (!pickupTime || !pickupTime.date) {
    return null;
  }

  try {
    const dateStr = pickupTime.date;
    const timeStr = pickupTime.time || pickupTime.timeString || '';

    if (!timeStr || timeStr === PickUpTimeType.ASAP) {
      return null;
    }

    const timeMoment = moment(timeStr, [
      'LT',
      'HH:mm',
      'HH:mm:ss',
      'h:mm A',
      'h:mm:ss A',
    ]);
    if (!timeMoment.isValid()) {
      return null;
    }

    const time24 = timeMoment.format('HH:mm');

    let dateMoment: moment.Moment;

    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      dateMoment = moment.tz(dateStr, 'YYYY-MM-DD', locationTimezone);
    } else {
      dateMoment = moment.tz(dateStr, locationTimezone);
      if (!dateMoment.isValid()) {
        const tempMoment = moment(dateStr);
        if (tempMoment.isValid()) {
          dateMoment = moment.tz(
            tempMoment.format('YYYY-MM-DD'),
            'YYYY-MM-DD',
            locationTimezone,
          );
        }
      }
    }

    if (!dateMoment.isValid()) {
      return null;
    }

    const dateOnly = dateMoment.format('YYYY-MM-DD');
    const fullDateTime = `${dateOnly} ${time24}:00`;

    const pickupDateTime = moment.tz(
      fullDateTime,
      'YYYY-MM-DD HH:mm:ss',
      locationTimezone,
    );

    if (!pickupDateTime.isValid()) {
      return null;
    }

    return pickupDateTime;
  } catch (error) {
    return null;
  }
};

const isWeekend = (dayOfWeek: DayOfWeek): boolean => {
  return dayOfWeek === DAYS.SUNDAY || dayOfWeek === DAYS.SATURDAY;
};

const isSubsidyValidForDateTime = (
  pantryGroup: PantryGroupDto | null | undefined,
  dateTime: moment.Moment | null,
): boolean => {
  try {
    if (!pantryGroup || !dateTime || !dateTime.isValid()) {
      return true;
    }

    const weekendBlocked =
      pantryGroup.isExcludesWeekend && isWeekend(dateTime.day() as DayOfWeek);

    const pastEndDate =
      pantryGroup.endDate &&
      dateTime
        .clone()
        .startOf('day')
        .isAfter(moment(pantryGroup.endDate, 'YYYY-MM-DD'), 'day');

    if (weekendBlocked || pastEndDate) {
      return false;
    }

    const trimmedRestrictions = pantryGroup.dayTimeRestrictions?.trim();
    if (!trimmedRestrictions) {
      return true;
    }

    const restrictions = parseDayTimeRestrictions(trimmedRestrictions);

    const dayRestriction = restrictions.find(
      (r) => r.dayOfWeek === dateTime.day(),
    );
    if (!dayRestriction) {
      return false;
    }

    if (dayRestriction.timeRanges.length === 0) {
      return false;
    }

    if (isFullDayRange(dayRestriction.timeRanges)) {
      return true;
    }

    const allowed = isTimeInRanges(
      dateTime.format('HH:mm'),
      dayRestriction.timeRanges,
    );
    return allowed;
  } catch (error) {
    return true;
  }
};

export const isSubsidyValidForPickupTime = (
  pantryGroup: PantryGroupDto | null | undefined,
  pickupTime: {date?: string; time?: string; timeString?: string} | null,
  locationTimezone: string,
): boolean => {
  if (!pickupTime?.date || pickupTime.time === PickUpTimeType.ASAP) {
    return true;
  }

  const dateTime = getPickupTimeInLocationTimezone(
    pickupTime,
    locationTimezone,
  );
  return isSubsidyValidForDateTime(pantryGroup, dateTime);
};

export const isSubsidyValidForCurrentTime = (
  pantryGroup: PantryGroupDto | null | undefined,
  locationTimezone: string,
): boolean => {
  const nowInLocation = moment.tz(locationTimezone);
  return isSubsidyValidForDateTime(pantryGroup, nowInLocation);
};

type SubsidyValidationMode = 'orderNow' | 'orderAhead' | 'auto';

export const canUseSubsidy = (
  balance:
    | {type: string; balance: number; isDisabled?: boolean}
    | null
    | undefined,
  locationTimezone: string,
  pantryGroup: PantryGroupDto | null | undefined,
  mode: SubsidyValidationMode = 'auto',
): boolean => {
  try {
    if (!balance || balance.balance <= 0 || balance.isDisabled) {
      return false;
    }

    const isSubsidy =
      balance.type === BalanceTypes.RollOver ||
      balance.type === BalanceTypes.TopOff;
    if (!isSubsidy) {
      return true;
    }

    if (!locationTimezone) {
      return true;
    }

    const pickupTimeFromStore = TransactionStore.getPickupTime();

    if (pickupTimeFromStore?.time === PickUpTimeType.ASAP) {
      return isSubsidyValidForCurrentTime(pantryGroup, locationTimezone);
    }

    const treatAsOrderAhead =
      mode === 'orderAhead' ||
      (mode === 'auto' &&
        pickupTimeFromStore &&
        pickupTimeFromStore.time !== PickUpTimeType.ASAP);

    if (!treatAsOrderAhead || !pickupTimeFromStore) {
      return isSubsidyValidForCurrentTime(pantryGroup, locationTimezone);
    }

    return isSubsidyValidForPickupTime(
      pantryGroup,
      pickupTimeFromStore,
      locationTimezone,
    );
  } catch (error) {
    return true;
  }
};
